
import React, { type FC, type MouseEvent, useState, useRef, useEffect, type ChangeEvent, type RefObject } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, type FieldArray, type FieldArrayMethodProps, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';

import { ISequenceDefinition, defaultSequenceDefinition } from './models/SequenceDefinition';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom, isSaveLoadingAtom } from 'library/store';
import useSequenceDefinitionService, { useBasicFilterSequenceDefinition } from './services/SequenceDefinition';
import { useRecoilState } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem } from '@mui/material';
import NumberFormat from 'react-number-format';

import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import { isFalsy } from 'utility-types';
import EntityExpression from 'components/ui/EntityExpression';

export const SequenceDefinitionForm: FC<ISequenceDefinition> = (props: ISequenceDefinition = defaultSequenceDefinition) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { id } = useParams();

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { retrieveEntity, retrieveData, openEntityActionDrawer,
    checkEntitySaveAuthorization, checkEntityExpressionSyntax } = entityService();

  const { createSequenceDefinition, updateSequenceDefinition } = useSequenceDefinitionService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterSequenceDefinition = useBasicFilterSequenceDefinition(
    (event: React.MouseEvent<unknown>, row: ISequenceDefinition) => {
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );


  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity(
    (event: React.MouseEvent<unknown>, row: IEntity) => {
      const { name, description } = row;

      setValue('entityName', name || '');
      setValue('entityDescription', description || '');

      setOpenEntityFilter(false);
    }
  );


  const methods = useForm<ISequenceDefinition>({ defaultValues: defaultSequenceDefinition });
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  const watchResetType = watch('resetType');
  const watchNumberExpression = watch('numberExpression');
  const watchEntityName = watch('entityName');


  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<ISequenceDefinition>, Error, ISequenceDefinition>(
    _id > 0 ? updateSequenceDefinition : createSequenceDefinition, {
    onSuccess: (data: IResult<ISequenceDefinition>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      _setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);
      setCurrentFormNameAtom(`${t('Extension type')} - # ${data.data.id} # -`);
      //queryClient.invalidateQueries(['SequenceDefinition', data.data.id]);
    },
    onError: (err: Error) => {
      setIsSaveLoading(false);
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });

    }
  });

  const { data: _data, refetch } = useQuery<ISequenceDefinition>(['SequenceDefinition', _id], () => retrieveEntity('SequenceDefinition', _id),
    { refetchOnWindowFocus: false, enabled: false });

  const [resetTypes,] = useState([
    { name: 'n', text: t('None'), value: 'n' },
    { name: 'y', text: t('Year'), value: 'y' },
    { name: 'm', text: t('Month'), value: 'm' },
    { name: 'd', text: t('Daily'), value: 'd' },
  ]);

  const handleClickSearchEntity = (event: any) => {
    setOpenEntityFilter(true);
  }

  const handleCloseBasicTextFilterEntityForm = () => {
    setOpenEntityFilter(false);
  }


  ///////
  const [openEntityExpression, setOpenEntityExpression] = useState(false);
  const [currentExpression, setCurrentExpression] = useState('');
  const [currentEntityName, setCurrentEntityName] = useState('');
  const [currentReturnType, setCurrentReturnType] = useState('');

  type ExpressionType = 'number';
  const [currentExpressionType, setCurrentExpressionType] = useState<ExpressionType>('number');

  const handleClickOpenExpression = (event: any, expressionType: ExpressionType,
    entityName: string, returnType: string, expression: string) => {

    if (isFalsy(entityName)) {
      enqueueSnackbar(t('Entity is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      return;
    }

    setCurrentExpressionType(expressionType);
    setCurrentEntityName(entityName);
    setCurrentReturnType(returnType);
    setCurrentExpression(expression);
    setOpenEntityExpression(true);
  }

  const handleClickOkExpression = async () => {

    const checkExpression = await checkEntityExpressionSyntax(currentEntityName, currentExpression);
    if (!checkExpression) {
      enqueueSnackbar(t('Expression evaluation error'), {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      return;
    }

    const { syntaxOk, syntaxError, returnType } = checkExpression;
    if (!syntaxOk) {
      enqueueSnackbar(syntaxError, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      return;
    }



    if (returnType !== currentReturnType) {
      enqueueSnackbar(`${t('The result of expression must be')} ${currentReturnType}`, {
        variant: 'error', anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      return;
    }

    if (['number'].includes(currentExpressionType)) {
      setValue('numberExpression', currentExpression, { shouldValidate: true });
    }

    setOpenEntityExpression(false);
  }


  //////


  useEffect(() => {
    setCurrentFormNameAtom(t('Sequence definition'));
    setCurrentBasicTextFilterProps(basicFilterSequenceDefinition);
  }, []);

  /********** This use effect call retreive data wich will call refetch and _data will be updated. 
    and the new useEffect will take place ********************/
  useEffect(() => {
    setCurrentFormNameAtom(t('Sequence definition'));

    if (_id > 0)
      retrieveData('SequenceDefinition', _id, refetch);
  }, [_id]);


  useEffect(() => {

    if (_data && _data.id > 0) {
      setCurrentFormNameAtom(_data.id > 0 ? `${t('Sequence definition')} - # ${_data.id} # -` : t('Sequence definition'));
      reset(_data);
    }
  }, [_data]);

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);
    reset(defaultSequenceDefinition);
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!checkEntitySaveAuthorization('SequenceDefinition', _id)) {
      setIsSaveLoading(false);
      return;
    }

    const data = getValues();
    if (data.name.trim() === '' || data.code.trim() === '') {
      enqueueSnackbar(t('Reference is not specified'), {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1500
      });
      setIsSaveLoading(false);
      return;
    }

    mutate(data);
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('SequenceDefinition', _id);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={3} columnSpacing={3}>
          <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >
              <Box sx={{ mt: 1, width: '100%' }} >
                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
                <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />


                <TextField sx={{ width: 'calc(40% - 8px)' }} id="name" label={t('Code')} {...register('code')}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <TextField sx={{ width: 'calc(60% - 8px)' }} id="entity" label={t('Entity')} 
                  inputProps={{ readOnly: true, autoComplete: 'new-password', style: { textTransform: 'none' } }}
                  {...register('entityDescription')}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton color="primary" onClick={handleClickSearchEntity}>
                          <ArrowDropDownCircleIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <TextField sx={{ width: 'calc(75% - 8px)' }} id="description" label={t('Name')}
                  {...register('name')}
                  inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }} />
                <FormControlLabel sx={{ width: `calc(25% - 8px)` }}
                  label={t('Is active ?')}
                  control={
                    <Controller
                      name='isActive' 
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
              </Box>
              <Box sx={{ mt: 3, width: '100%' }} >
                <Controller
                  render={({ field: { onChange, onBlur, name, value, ref } }) => {
                    return (
                      <NumberFormat sx={{ width: 'calc(25% - 8px)' }}
                        label={t('Start value')}
                        //decimalScale={2}
                        allowEmptyFormatting={false}
                        control={control}

                        thousandSeparator={true}
                        onValueChange={(v) => onChange(v.floatValue)}
                        //{...field}
                        customInput={TextFieldRight}
                        defaultValue={value}
                        value={value}
                      />
                    );
                  }}
                  name='startValue'
                  control={control}
                />
                <Controller
                  render={({ field: { onChange, onBlur, name, value, ref } }) => {
                    return (
                      <NumberFormat sx={{ width: 'calc(25% - 8px)' }}
                        label={t('Increment by')}
                        //decimalScale={2}
                        allowEmptyFormatting={false}
                        control={control}

                        thousandSeparator={true}
                        onValueChange={(v) => onChange(v.floatValue)}
                        //{...field}
                        customInput={TextFieldRight}
                        defaultValue={value}
                        value={value}
                      />
                    );
                  }}
                  name='incrementBy'
                  control={control}
                />
                <Controller name='resetType' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="type"
                      label={t('Reset type')}
                      inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}>
                      {resetTypes.map(
                        (x, idx) => <MenuItem key={x.value} value={x.value}>{x.text}</MenuItem>)
                      }
                    </TextField>
                  )}
                />

                {openEntityFilter && <FormDialog open={openEntityFilter} maxWidth='md'
                  okText={t('Close')} cancelText='' title={t('Entity filter')} onCancel={() => { }}
                  onClose={() => { setOpenEntityFilter(false); }} onOk={handleCloseBasicTextFilterEntityForm}  >
                  <BasicTextFilterForm<IEntity> {...basicFilterEntity} />
                </FormDialog>}
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller
                  key={`query-x`}
                  render={({ field }) => <TextField label={t('Numbering')} {...field} multiline={true} rows={2}
                    sx={{ width: `calc(100% - 8px)`, textTransform: 'none' }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton color="primary" onClick={(event) => handleClickOpenExpression(event, 'number', watchEntityName, 'string', watchNumberExpression)}>
                            <DeveloperModeIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}

                  />}
                  name={`numberExpression`}
                  control={control}
                />
              </Box>
              {openEntityExpression && <FormDialog open={openEntityExpression} maxWidth='md'
                okText={t('OK')} cancelText='' title={t('Expression')} onCancel={() => { }}
                onClose={() => { setOpenEntityExpression(false); }} onOk={handleClickOkExpression}  >
                <EntityExpression entityName={currentEntityName} properties={[]}
                  expression={currentExpression} setExpression={setCurrentExpression} />
              </FormDialog>}
            </Stack>
          </Grid>

        </Grid>
      </Box>
    </FormProvider>
  )
}

